import FetchClient, { type ApiResponse } from "./FetchClient";

export interface CommunitySuccessPost {
  postId: string;
  authorName: string;
  authorAvatarUrl?: string;
  imageUrl: string;
  imageUrls: string[];
  message: string;
  createdAt: number;
  commentCount: number;
  likeCount: number;
  liked?: boolean;
  mine?: boolean;
  source?: "discord";
}

export interface CommunitySuccessComment {
  commentId: string;
  postId: string;
  authorName: string;
  authorAvatarUrl?: string;
  message: string;
  createdAt: number;
  mine?: boolean;
}

interface CommunityPostsResponse {
  posts: CommunitySuccessPost[];
  nextCursor?: string;
  hasMore?: boolean;
}

interface CommunityCommentsResponse {
  comments: CommunitySuccessComment[];
  nextCursor?: string;
  hasMore?: boolean;
}

export function getCommunitySuccessesQueryKey(pageSize = 20): string[] {
  return ["community-successes", `page-size-${pageSize}`];
}

export async function getCommunitySuccesses(pageSize = 20, cursor?: string): Promise<CommunityPostsResponse> {
  const params = new URLSearchParams({ limit: String(pageSize) });
  if (cursor) params.set("cursor", cursor);
  const response = await FetchClient.get<ApiResponse<CommunityPostsResponse>>(
    `/community-successes?${params.toString()}`
  );
  return {
    posts: Array.isArray(response.data?.posts) ? response.data.posts : [],
    nextCursor: response.data?.nextCursor,
    hasMore: Boolean(response.data?.hasMore),
  };
}

export async function createCommunitySuccess(photos: File[], message: string): Promise<CommunitySuccessPost> {
  if (photos.length < 1 || photos.length > 5) {
    throw new Error("Add between 1 and 5 photos.");
  }
  const form = new FormData();
  for (const photo of photos) form.append("photos", photo);
  form.append("message", message.trim());
  form.append("guidelinesAccepted", "true");
  const response = await FetchClient.postForm<ApiResponse<CommunitySuccessPost>>(
    "/community-successes",
    form
  );
  return response.data;
}

export async function getCommunityComments(postId: string, cursor?: string): Promise<CommunityCommentsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const response = await FetchClient.get<ApiResponse<CommunityCommentsResponse>>(
    `/community-successes/${encodeURIComponent(postId)}/comments${suffix}`
  );
  return {
    comments: Array.isArray(response.data?.comments) ? response.data.comments : [],
    nextCursor: response.data?.nextCursor,
    hasMore: Boolean(response.data?.hasMore),
  };
}

export async function createCommunityComment(postId: string, message: string, guidelinesAccepted: boolean): Promise<CommunitySuccessComment> {
  const response = await FetchClient.post<ApiResponse<CommunitySuccessComment>>(
    `/community-successes/${encodeURIComponent(postId)}/comments`,
    { message: message.trim(), guidelinesAccepted }
  );
  return response.data;
}

export type CommunityReportReason = "spam" | "harassment" | "unsafe" | "other";

export async function toggleCommunityLike(postId: string): Promise<{ liked: boolean; likeCount: number }> {
  const response = await FetchClient.post<ApiResponse<{ liked: boolean; likeCount: number }>>(
    `/community-successes/${encodeURIComponent(postId)}/like`,
    {}
  );
  return { liked: Boolean(response.data?.liked), likeCount: Number(response.data?.likeCount) || 0 };
}

export async function reportCommunityContent(
  postId: string,
  targetType: "post" | "comment",
  targetId: string,
  reason: CommunityReportReason
): Promise<void> {
  await FetchClient.post(
    `/community-successes/${encodeURIComponent(postId)}/report`,
    { targetType, targetId, reason }
  );
}

export async function blockCommunityPostAuthor(postId: string): Promise<void> {
  await FetchClient.post(`/community-successes/${encodeURIComponent(postId)}/block`, {});
}

export async function reportCommunityPostAuthor(postId: string, reason: CommunityReportReason): Promise<void> {
  await FetchClient.post(`/community-successes/${encodeURIComponent(postId)}/report-author`, { reason });
}

export async function blockCommunityCommentAuthor(postId: string, commentId: string): Promise<void> {
  await FetchClient.post(
    `/community-successes/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/block`,
    {}
  );
}

export async function reportCommunityCommentAuthor(postId: string, commentId: string, reason: CommunityReportReason): Promise<void> {
  await FetchClient.post(
    `/community-successes/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/report-author`,
    { reason }
  );
}

export async function deleteCommunitySuccess(postId: string): Promise<void> {
  await FetchClient.delete(`/community-successes/${encodeURIComponent(postId)}`);
}

export async function deleteCommunityComment(postId: string, commentId: string): Promise<void> {
  await FetchClient.delete(
    `/community-successes/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`
  );
}

/** A member haul the logged-out home page may show: photo and caption only, no author. */
export interface PublicMemberHaul {
  postId: string;
  imageUrl: string;
  message: string;
  createdAt: number;
}

/** The home page's short member-hauls teaser. The full feed stays member-only. */
export async function getPublicMemberHauls(): Promise<PublicMemberHaul[]> {
  const response = await FetchClient.get<ApiResponse<{ posts?: PublicMemberHaul[] }>>("/public/member-hauls", {}, "optional");
  const posts = Array.isArray(response.data?.posts) ? response.data.posts : [];
  return posts.filter((post) => typeof post.imageUrl === "string" && post.imageUrl && typeof post.message === "string");
}
